import {
  push
} from 'react-router-redux';
import {
  call,
  put,
  select,
  take,
  takeLatest
} from 'redux-saga/effects';
import {
  getPlanFromVPC
} from 'utils/vpc';
import { sendInitialAnalytics } from 'domain/Analytics/sagas';
import {
  setLoadPlanFromLinkError,
  showPriceDifferenceDialog,
  setLoadLocalPlanError,
  planLoading as applicationPlanLoading,
  showNewFeaturesToast
} from 'Application/actions';
import {
  KIOSK
} from 'Application/constants';
import {
  applicationModeSelector
} from 'Application/selectors';
import homePageMessages from 'pages/HomePage/messages';
import {
  currencySelector
} from 'domain/LanguageProvider/selectors';
import {
  hasLoadedOldPlanSelector,
  getPlanNameSelector
} from 'domain/LocalStorage/selectors';
import {
  setHasSeenNewFeatures,
  setHasLoadedOldPlan
} from 'domain/LocalStorage/actions';
import {
  LOAD_PLAN,
  OPTIONS_LOADED
} from '../constants';
import {
  planLoaded,
  planLoadFailed,
  planLoading,
  setPlanName,
  setPlanCode
} from '../actions';
import {
  planHasUnavailableOptionsSelector,
  conflictingAndAutomaticParametersSelector
} from '../selectors/resolver';
import {
  loadedLanguageSelector
} from '../selectors';
import {
  splashback
} from '../planParameters';
import {
  totalPriceSelector,
  totalPriceInclFamilyPriceSelector,
} from '../itemList';

export const isOldPlan = (plan) => (!plan.parameters.some((p) => p.name === splashback));

function deserializePrice(price) {
  if (price) {
    return Number(price.replace(':', '.'));
  } else {
    return undefined;
  }
}

function getParameterValue(plan, name) {
  const parameter = plan.parameters.find((p) => p.name === name) || {};
  return parameter.value;
}

export function* checkPriceDifference(plan, locale) {
  const oldPrice = deserializePrice(getParameterValue(plan, 'price'));
  const oldFamilyPrice = deserializePrice(getParameterValue(plan, 'familyPrice'));
  const oldCountry = getParameterValue(plan, 'country');
  const oldCurrency = getParameterValue(plan, 'currency');
  const currentPrice = yield select(totalPriceSelector);
  const currentPriceInclFamilyPrice = yield select(totalPriceInclFamilyPriceSelector);
  const currentCurrency = yield select(currencySelector);
  if ((currentPrice && oldPrice && oldPrice !== currentPrice) || (oldCountry !== locale.country && oldCurrency !== currentCurrency) || (currentPriceInclFamilyPrice && oldFamilyPrice && oldFamilyPrice !== currentPriceInclFamilyPrice)) {
    yield put(showPriceDifferenceDialog(oldPrice, oldFamilyPrice, oldCurrency, oldCountry));
  }
}


export function* loadPlan(action) {
  const locale = yield select(loadedLanguageSelector);
  try {
    yield put(applicationPlanLoading(true));
    const result = yield call(getPlanFromVPC, locale, action.code);
    const isRunningInCTEF = window.location.href.includes('cte');
    if(!isRunningInCTEF){
      yield sendInitialAnalytics(action.code);
    }
    const plan = result.configuration.content;
    const oldPlan = yield call(isOldPlan, plan);
    const hasLoadedOldPlan = yield select(hasLoadedOldPlanSelector);
    const applicationMode = yield select(applicationModeSelector);
    if (oldPlan && (!hasLoadedOldPlan || applicationMode === KIOSK)) {
      yield put(setHasLoadedOldPlan(true));
      yield put(setHasSeenNewFeatures(false));
      yield put(showNewFeaturesToast(true));
    }
    if (plan !== null && plan !== undefined) {
      yield put(planLoaded(plan, false, action.code));
      if (!action.fromLink) {
        const hasUnavailableOptions = yield select(planHasUnavailableOptionsSelector);
        const conflicts = yield select(conflictingAndAutomaticParametersSelector);
        if (hasUnavailableOptions || conflicts.size > 0) {
          yield put(push('/main/style'));
        } else {
          yield put(setPlanCode(action.code));
          yield put(push('/itemlist'));
        }
      }
      const getPlanName = yield select(getPlanNameSelector);
      const planName = getPlanName(action.code);
      yield put(setPlanName(planName));

      yield call(checkPriceDifference, plan, locale);
    }
    else{
      yield put(push('/main/style'));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load plan=', action.code, ' error=', error);
    if (action.fromLink) {
      yield put(push('/'));
      yield put(setLoadPlanFromLinkError(true));
    } else if (action.fromLocalStorage) {
      yield put(setLoadLocalPlanError(true));
    } else {
      yield put(planLoadFailed(homePageMessages.loadFailed));
    }
  } finally {
    yield put(applicationPlanLoading(false));
  }
}

export default function* loadPlanSaga() {
  const action = yield take(LOAD_PLAN);
  if (action.fromLink) {
    yield put(planLoading(true));
    yield take(OPTIONS_LOADED);
    yield call(loadPlan, action);
    yield put(planLoading(false));
  } else {
    yield call(loadPlan, action);
  }

  // After first we just take latest
  yield takeLatest(LOAD_PLAN, loadPlan);
}
